/**
 * useConversationActions - Hook para ações de conversas
 * Centraliza operações de transferência, finalização, etc.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  transferConversation,
  closeConversation,
  reopenConversation,
} from '../services/chatService';
import type {
  ConversationResponse,
  TransferRequest,
  CloseConversationRequest,
} from '@troia-v3/types';

interface UseConversationActionsOptions {
  conversationId: string;
  onTransferSuccess?: () => void;
  onCloseSuccess?: () => void;
  onReopenSuccess?: () => void;
}

export const useConversationActions = (options: UseConversationActionsOptions) => {
  const { conversationId, onTransferSuccess, onCloseSuccess, onReopenSuccess } = options;
  const queryClient = useQueryClient();

  // ============================================================================
  // Transfer Conversation Mutation
  //
  // Endpoint padronizado: POST /conversations/:id/transfer
  // Body: { toUserId?, toTeamId?, reason?, notes? } — XOR, null desatribui.
  // Cobre tanto atribuição de user quanto de team via o mesmo payload.
  // ============================================================================
  const transferMutation = useMutation({
    mutationFn: (data: TransferRequest) =>
      transferConversation(conversationId, data),

    onSuccess: (_conversation: ConversationResponse) => {
      toast.success('Conversa transferida com sucesso');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['attendance-management'] }); // ✅ Invalidate attendance management cache
      queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] }); // ✅ Invalidate SP4 kanban board cache
      onTransferSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao transferir conversa');
      console.error('Transfer conversation error:', error);
    },
  });

  // ============================================================================
  // Close Conversation Mutation
  // ============================================================================
  const closeMutation = useMutation({
    mutationFn: (data?: CloseConversationRequest) =>
      closeConversation(conversationId, data),

    onSuccess: () => {
      toast.success('Conversa finalizada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['attendance-management'] }); // ✅ Invalidate attendance management cache
      queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] }); // ✅ Invalidate SP4 kanban board cache

      onCloseSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao finalizar conversa');
      console.error('Close conversation error:', error);
    },
  });

  // ============================================================================
  // Reopen Conversation Mutation
  // ============================================================================
  const reopenMutation = useMutation({
    mutationFn: () => reopenConversation(conversationId),

    onSuccess: () => {
      toast.success('Conversa reaberta com sucesso');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['attendance-management'] }); // ✅ Invalidate attendance management cache
      queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] }); // ✅ Invalidate SP4 kanban board cache
      onReopenSuccess?.();
    },

    onError: (error) => {
      toast.error('Erro ao reabrir conversa');
      console.error('Reopen conversation error:', error);
    },
  });

  return {
    // Transfer (cobre atribuição de user ou team via TransferRequest)
    transferConversation: transferMutation.mutate,
    transferConversationAsync: transferMutation.mutateAsync,
    isTransferring: transferMutation.isPending,

    // Close
    closeConversation: closeMutation.mutate,
    closeConversationAsync: closeMutation.mutateAsync,
    isClosing: closeMutation.isPending,

    // Reopen
    reopenConversation: reopenMutation.mutate,
    reopenConversationAsync: reopenMutation.mutateAsync,
    isReopening: reopenMutation.isPending,
  };
};
